import './InternalSecondaryMobile.css';
// src/MaintenanceReviews.js
//
// Internal Reviews page — lives at /maintenance/reviews.
//
// Data flow ("Option B — public Places API + LLM crediting"):
//   • Reviews come from /api/reviews/internal/all (backed by
//     app/internal_reviews.py). That endpoint polls the public Google
//     Places API (latest 5 at a time) and stores each new review in the
//     Back4App `InternalGoogleReview` class. Auto-syncs on stale page
//     load; a manual "Sync now" button is here too.
//   • Each new review is run through gpt-4o-mini server-side to figure
//     out which technician(s) it credits. The result is stored as
//     `matched_techs` on the review row, so the leaderboard is just an
//     aggregation — no LLM work at render time.
//   • Total maintenance counts (for the reviews-per-maintenance metric)
//     are still pulled from /api/getTechStartTimes.
//   • A public, shareable version of the leaderboard lives at
//     /leaderboard (PublicLeaderboard.js). No password required.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaCommentDots,
  FaStar,
  FaStarHalfAlt,
  FaRegStar,
  FaTrophy,
  FaMedal,
  FaUserCircle,
  FaExternalLinkAlt,
  FaGoogle,
  FaShareAlt,
  FaSpinner,
  FaCheckCircle,
  FaTimes,
  FaBolt,
  FaSyncAlt,
  FaReply,
  FaSearch,
  FaChevronDown,
} from 'react-icons/fa';
// See note in MaintenanceHome.js — use the shared config so production
// builds never pick up the local-dev REACT_APP_API_URL=localhost:5051.
import API_BASE_URL from './config/api';

// Palette aligned with MaintenanceHome / ManageAccounts so the hub
// feels cohesive across tabs.
const C = {
  navy: '#1F3A68',
  navySoft: '#2C4E84',
  ink: '#1a2236',
  inkSoft: '#374151',
  muted: '#6b7280',
  line: '#E6E8EC',
  lineSoft: '#EFF2F7',
  bg: '#F4F7FB',
  card: '#ffffff',
  green: '#1c9550',
  greenSoft: '#e3f5ea',
  blue: '#2563eb',
  blueSoft: '#eff6ff',
  amber: '#C68A1C',
  amberSoft: '#fdf3df',
  amberBorder: '#fcd34d',
  amberInk: '#92400e',
  red: '#B62525',
  redSoft: '#fbe2e2',
  purple: '#7c3aed',
  gold: '#d4a017',
  silver: '#94a3b8',
  bronze: '#a0522d',
  shadow: '0 1px 3px rgba(20,30,60,.06), 0 8px 24px rgba(20,30,60,.04)',
};
const sharedFont =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif';

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

// Star ribbon. Supports half-stars; size + color are tweakable so we
// can reuse it both in review cards and in the headline pill.
function StarRow({ rating = 0, size = 14, color = C.gold }) {
  const stars = [];
  const r = Math.max(0, Math.min(5, Number(rating) || 0));
  for (let i = 1; i <= 5; i++) {
    if (r >= i) stars.push(<FaStar key={i} style={{ color, fontSize: size }} />);
    else if (r >= i - 0.5) stars.push(<FaStarHalfAlt key={i} style={{ color, fontSize: size }} />);
    else stars.push(<FaRegStar key={i} style={{ color: '#e5e7eb', fontSize: size }} />);
  }
  return <span style={{ display: 'inline-flex', gap: 2 }}>{stars}</span>;
}

function MiniSpark({ points, color = C.navy, height = 32, width = 140 }) {
  if (!points || points.length < 2) {
    return (
      <div style={{ height, width, color: C.muted, fontSize: 11, display: 'flex', alignItems: 'center' }}>
        not enough data
      </div>
    );
  }
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const step = width / (points.length - 1);
  const coords = points.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y];
  });
  const path = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      <path d={path} fill="none" stroke={color} strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={coords[coords.length - 1][0]} cy={coords[coords.length - 1][1]} r={2.3} fill={color} />
    </svg>
  );
}

// "3 days ago" / "5 mo ago".
const relativeAgo = (iso) => {
  if (!iso) return '';
  const t = typeof iso === 'number' ? iso : new Date(iso).getTime() / 1000;
  if (Number.isNaN(t)) return '';
  const diff = (Date.now() / 1000 - t) / 60;
  if (diff < 60) return `${Math.max(1, Math.round(diff))}m ago`;
  if (diff < 60 * 24) return `${Math.round(diff / 60)}h ago`;
  if (diff < 60 * 24 * 30) return `${Math.round(diff / 60 / 24)}d ago`;
  if (diff < 60 * 24 * 365) return `${Math.round(diff / 60 / 24 / 30)}mo ago`;
  return `${Math.round(diff / 60 / 24 / 365)}y ago`;
};

// Convert createTime (ISO) -> Date for sorting/bucketing.
const toDate = (iso) => (iso ? new Date(iso) : null);
const monthKeyFromDate = (d) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : null;

// Best-effort attribution: scan a review's text for any technician
// name we know about. Returns the list of matches (often empty, often
// just one). Names are matched case-insensitively, on word boundary,
// 3-char min to avoid false positives ("Al", "Ed", etc.).
function detectTechs(text, knownTechs) {
  if (!text || !knownTechs || !knownTechs.length) return [];
  const lc = String(text).toLowerCase();
  const hits = new Set();
  for (const tech of knownTechs) {
    const display = String(tech || '').trim();
    if (!display) continue;
    const tokens = display.split(/\s+/).filter(Boolean);
    const candidates = new Set();
    candidates.add(display.toLowerCase());
    if (tokens[0]) candidates.add(tokens[0].toLowerCase());
    for (const cand of candidates) {
      if (cand.length < 3) continue;
      const re = new RegExp(
        `(^|[^a-z])${cand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z]|$)`,
        'i',
      );
      if (re.test(lc)) { hits.add(display); break; }
    }
  }
  return Array.from(hits);
}

// Bucket reviews into monthly bins for the headline sparklines (most-
// recent 12 months, oldest -> newest).
function bucketByMonth(reviews, months = 12) {
  const out = new Array(months).fill(0);
  const now = new Date();
  for (const r of reviews) {
    const d = toDate(r.createTime);
    if (!d) continue;
    const diffMonths = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
    if (diffMonths >= 0 && diffMonths < months) {
      out[months - 1 - diffMonths] += 1;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function MaintenanceReviews() {
  const [feed, setFeed] = useState(null); // { connected, summary, reviews, oauth }
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedError, setFeedError] = useState(null);

  const [tech, setTech] = useState(null);
  const [techLoading, setTechLoading] = useState(true);

  const [shareSnackbar, setShareSnackbar] = useState(false);
  const [selectedTech, setSelectedTech] = useState('all');
  const [textFilter, setTextFilter] = useState('');
  // Month scope for both the reviews list and the leaderboard:
  // 'all' (all time) or a month key like '2026-07'.
  const [monthFilter, setMonthFilter] = useState('all');

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  // --- fetchers ---
  const fetchFeed = useCallback(async () => {
    setFeedLoading(true);
    setFeedError(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/reviews/internal/all`);
      const d = await r.json();
      setFeed(d);
    } catch (e) {
      console.error('[MaintenanceReviews] feed fetch failed', e);
      setFeedError(e.message || 'Could not load reviews');
    } finally {
      setFeedLoading(false);
    }
  }, []);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  useEffect(() => {
    let cancelled = false;
    setTechLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/getTechStartTimes?lookback_days=90&exclude_outliers=true`);
        const d = await r.json();
        if (!cancelled) setTech(d);
      } catch (e) {
        console.error('[MaintenanceReviews] tech fetch failed', e);
        if (!cancelled) setTech(null);
      } finally {
        if (!cancelled) setTechLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Listen for the OAuth popup → "connected" handshake so we can
  // refresh the feed without the user clicking around.
  useEffect(() => {
    const onMsg = (e) => {
      if (e?.data?.gbp === 'connected') {
        fetchFeed();
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [fetchFeed]);

  // --- derived ---
  const reviews = useMemo(() => Array.isArray(feed?.reviews) ? feed.reviews : [], [feed]);
  const summary = feed?.summary || {};
  const connected = !!feed?.connected;

  const knownTechs = useMemo(() => {
    if (!tech?.technicians_list) return [];
    return tech.technicians_list
      .map((t) => (typeof t === 'string' ? t : t?.name || ''))
      .filter(Boolean);
  }, [tech]);

  // Trust the backend's `matched_techs` (computed server-side by
  // gpt-4o-mini against the canonical roster). Only fall back to the
  // local regex matcher if the backend returned nothing for a row
  // (e.g. older rows pre-dating LLM crediting).
  const enrichedReviews = useMemo(
    () => reviews.map((r) => {
      const fromServer = Array.isArray(r.matched_techs) ? r.matched_techs : [];
      if (fromServer.length > 0) return r;
      return { ...r, matched_techs: detectTechs(r.comment, knownTechs) };
    }),
    [reviews, knownTechs],
  );

  // Epoch (ms) when we started tracking reviews internally. Pulled
  // from the backend (min(firstSeenAt) across stored rows). Used as
  // the lower bound for the "reviews per maintenance visit" metric so
  // numerator and denominator cover the same window.
  const trackingSinceMs = useMemo(() => {
    const sec = summary?.trackingSince;
    if (typeof sec !== 'number' || sec <= 0) return null;
    return sec * 1000;
  }, [summary]);

  // Units maintained from when we started tracking reviews → today.
  const unitsSinceTracking = useMemo(() => {
    const series = Array.isArray(tech?.daily_summary) ? tech.daily_summary : [];
    if (!trackingSinceMs) {
      return series.reduce((s, r) => s + (Number(r.unit_count) || 0), 0);
    }
    return series.reduce((s, r) => {
      const d = new Date(`${r.date}T00:00:00`).getTime();
      if (!d || d < trackingSinceMs) return s;
      return s + (Number(r.unit_count) || 0);
    }, 0);
  }, [tech, trackingSinceMs]);

  // All reviews since we started tracking (i.e. everything we have).
  const reviewsSinceTracking = reviews.length;

  // Distinct months present in the review data (newest first) for the picker.
  const availableMonths = useMemo(() => {
    const seen = new Map();
    for (const r of enrichedReviews) {
      const d = toDate(r.createTime);
      const key = monthKeyFromDate(d);
      if (!key) continue;
      if (!seen.has(key)) {
        seen.set(key, d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
      }
    }
    return Array.from(seen.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([key, label]) => ({ key, label }));
  }, [enrichedReviews]);

  const leaderboard = useMemo(() => {
    const map = new Map();
    knownTechs.forEach((t) => map.set(t, { tech: t, count: 0, ratingSum: 0, ratingN: 0, lastTime: 0 }));
    for (const r of enrichedReviews) {
      if (monthFilter !== 'all' && monthKeyFromDate(toDate(r.createTime)) !== monthFilter) continue;
      for (const t of r.matched_techs) {
        if (!map.has(t)) map.set(t, { tech: t, count: 0, ratingSum: 0, ratingN: 0, lastTime: 0 });
        const e = map.get(t);
        e.count += 1;
        if (typeof r.starRating === 'number') { e.ratingSum += r.starRating; e.ratingN += 1; }
        const tt = toDate(r.createTime)?.getTime() || 0;
        if (tt > e.lastTime) e.lastTime = tt;
      }
    }
    const list = Array.from(map.values())
      .map((e) => ({
        tech: e.tech,
        count: e.count,
        avgRating: e.ratingN ? e.ratingSum / e.ratingN : null,
        lastTime: e.lastTime || null,
      }))
      .filter((e) => e.count > 0);
    list.sort((a, b) =>
      b.count - a.count
      || (b.avgRating ?? 0) - (a.avgRating ?? 0)
      || (b.lastTime ?? 0) - (a.lastTime ?? 0)
      || a.tech.localeCompare(b.tech),
    );
    return list;
  }, [enrichedReviews, knownTechs, monthFilter]);

  // Filters applied to the reviews list. NOTE: the "this month" filter
  // still buckets by original post date (createTime) so the month math is
  // unchanged — but we SORT by most-recent activity (max of post/edit
  // time) so a recently-edited older review floats to the top instead of
  // being buried by its original date.
  const visibleReviews = useMemo(() => {
    const lcQuery = textFilter.trim().toLowerCase();
    const activityMs = (r) => {
      const c = toDate(r.createTime)?.getTime() || 0;
      const u = r.updateEpoch ? r.updateEpoch * 1000 : (toDate(r.updateTime)?.getTime() || 0);
      return Math.max(c, u);
    };
    const filtered = enrichedReviews.filter((r) => {
      if (selectedTech !== 'all' && !r.matched_techs.includes(selectedTech)) return false;
      if (monthFilter !== 'all' && monthKeyFromDate(toDate(r.createTime)) !== monthFilter) return false;
      if (lcQuery) {
        const blob = `${r.authorName || ''} ${r.comment || ''}`.toLowerCase();
        if (!blob.includes(lcQuery)) return false;
      }
      return true;
    });
    return filtered.slice().sort((a, b) => activityMs(b) - activityMs(a));
  }, [enrichedReviews, selectedTech, monthFilter, textFilter]);

  // Pill values
  const totalCount = summary.totalReviewCount ?? reviews.length ?? 0;
  const avgRating = summary.averageRating;
  const reviewsPerMaint = useMemo(() => {
    if (!unitsSinceTracking) return null;
    if (reviewsSinceTracking > 0) return reviewsSinceTracking / unitsSinceTracking;
    return null;
  }, [reviewsSinceTracking, unitsSinceTracking]);

  const totalSpark = useMemo(() => {
    const monthly = bucketByMonth(reviews, 12);
    // Cumulative running count so the line trends upward to today's total.
    const out = [];
    let acc = 0;
    for (const v of monthly) { acc += v; out.push(acc); }
    return out;
  }, [reviews]);

  const rpmSpark = useMemo(() => {
    const monthly = bucketByMonth(reviews, 12);
    const daily = Array.isArray(tech?.daily_summary) ? tech.daily_summary : [];
    const unitMonths = new Array(12).fill(0);
    const now = new Date();
    for (const row of daily) {
      const d = new Date(`${row.date}T00:00:00`);
      const diff = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
      if (diff >= 0 && diff < 12) unitMonths[12 - 1 - diff] += Number(row.unit_count) || 0;
    }
    return monthly.map((rv, i) => (unitMonths[i] > 0 ? rv / unitMonths[i] : 0));
  }, [reviews, tech]);

  const handleShare = useCallback(async () => {
    const url = `${window.location.origin}/leaderboard`;
    try {
      await navigator.clipboard.writeText(url);
      setShareSnackbar(true);
      setTimeout(() => setShareSnackbar(false), 1800);
    } catch (_) {
      window.prompt('Copy this link:', `${window.location.origin}/leaderboard`);
    }
  }, []);

  const handleSync = useCallback(async () => {
    const token = localStorage.getItem('managerToken') || '';
    if (!token) {
      setSyncMsg({ ok: false, text: 'You need to be logged in to sync.' });
      setTimeout(() => setSyncMsg(null), 6000);
      return;
    }
    setSyncing(true);
    setSyncMsg(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/reviews/internal/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!d.ok) {
        setSyncMsg({ ok: false, text: d.error || 'Sync failed' });
      } else {
        setSyncMsg({
          ok: true,
          text: `Synced — ${d.fetched} pulled (${d.created} new, ${d.updated} refreshed).${d.totalReviewCount ? ` Total on Google: ${d.totalReviewCount}.` : ''}`,
        });
        await fetchFeed();
      }
    } catch (e) {
      setSyncMsg({ ok: false, text: e.message || 'Sync exception' });
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMsg(null), 6000);
    }
  }, [fetchFeed]);

  const handleOpenPublicLeaderboard = useCallback(() => {
    window.open(`${window.location.origin}/leaderboard`, '_blank', 'noopener');
  }, []);

  // --- presentation ---
  return (
    <div className="internal-secondary-page internal-reviews-page" style={{ background: C.bg, minHeight: '100vh', padding: '1.5rem 2rem 4rem', fontFamily: sharedFont, color: C.ink }}>
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 22 }}>
          <div>
            <div style={{ fontSize: 18, color: C.navy, fontWeight: 700, marginBottom: 6 }}>
              Customer Reviews
            </div>
            <h1 style={{ margin: 0, fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em' }}>
              Reviews & Technician Leaderboard
            </h1>
            <div style={{ color: C.muted, fontSize: 14, marginTop: 4 }}>
              Customer reviews archive and technician leaderboard
              {connected && summary.lastSyncAt
                ? <> · last sync {relativeAgo(summary.lastSyncAt)}</>
                : null}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleSync}
              disabled={syncing || !connected}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: connected ? C.card : C.lineSoft,
                border: `1px solid ${C.line}`,
                color: connected ? C.inkSoft : C.muted,
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 14px',
                borderRadius: 8,
                cursor: connected ? 'pointer' : 'not-allowed',
                boxShadow: C.shadow,
                opacity: syncing ? 0.6 : 1,
              }}
              title={connected ? 'Pull the latest 5 reviews from Google right now' : 'Add GOOGLE_PLACES_API_KEY to Heroku first'}
            >
              <FaSyncAlt className={syncing ? 'hudson-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync now'}
            </button>
            <button
              type="button"
              onClick={handleOpenPublicLeaderboard}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: C.card,
                border: `1px solid ${C.line}`,
                color: C.inkSoft,
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                boxShadow: C.shadow,
              }}
              title="Open the public, no-login leaderboard in a new tab"
            >
              <FaExternalLinkAlt /> Public leaderboard
            </button>
            <button
              type="button"
              onClick={handleShare}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: C.navy,
                border: 'none',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                boxShadow: C.shadow,
              }}
            >
              <FaShareAlt /> Share leaderboard
            </button>
          </div>
        </div>

        {/* Sync status flash */}
        {syncMsg && (
          <div
            style={{
              marginBottom: 14,
              background: syncMsg.ok ? C.greenSoft : C.redSoft,
              border: `1px solid ${syncMsg.ok ? '#bbf7d0' : '#fecaca'}`,
              color: syncMsg.ok ? '#14532d' : '#7f1d1d',
              padding: '10px 14px',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {syncMsg.ok ? <FaCheckCircle /> : <FaTimes />} {syncMsg.text}
          </div>
        )}

        {/* Places API not configured warning */}
        {!connected && (
          <div
            style={{
              marginBottom: 14,
              background: C.amberSoft,
              border: `1px solid ${C.amberBorder}`,
              color: C.amberInk,
              padding: '10px 14px',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            ⚠ Server is missing <code>GOOGLE_PLACES_API_KEY</code>. Add it to
            Heroku (and optionally <code>HUDSON_PLACE_ID</code> +{' '}
            <code>OPENAI_API_KEY</code>) to start tracking.
          </div>
        )}


        {/* Pills */}
        <div className="internal-mobile-stack"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 14,
            marginBottom: 18,
          }}
        >
          <Pill
            label="Total Google reviews"
            value={feedLoading ? '—' : (typeof totalCount === 'number' ? totalCount.toLocaleString() : '—')}
            sub={
              feedLoading
                ? 'Loading…'
                : connected
                  ? `${(summary.archivedCount ?? 0).toLocaleString()} archived locally`
                  : 'Not connected'
            }
            icon={<FaCommentDots />}
            accent={C.navy}
            spark={totalSpark}
            sparkColor={C.navy}
          />
          <Pill
            label="Average rating"
            value={typeof avgRating === 'number' ? avgRating.toFixed(1) : '—'}
            sub={
              feedLoading
                ? 'Loading…'
                : typeof avgRating !== 'number'
                  ? 'No rating yet'
                  : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <StarRow rating={avgRating} size={13} /> across {(totalCount ?? 0).toLocaleString()} reviews
                    </span>
            }
            icon={<FaStar />}
            accent={C.amber}
          />
          <Pill
            label="Reviews per maintenance visit"
            value={
              reviewsPerMaint == null
                ? '—'
                : `${(reviewsPerMaint * 100).toFixed(reviewsPerMaint < 0.01 ? 2 : 1)}%`
            }
            sub={
              techLoading
                ? 'Loading…'
                : !unitsSinceTracking
                  ? 'No maintenance data yet'
                  : reviewsSinceTracking > 0
                    ? `${reviewsSinceTracking} reviews · ${unitsSinceTracking.toLocaleString()} units · since tracking began${
                        trackingSinceMs
                          ? ` (${relativeAgo(trackingSinceMs / 1000)})`
                          : ''
                      }`
                    : `0 reviews · ${unitsSinceTracking.toLocaleString()} units · since tracking began`
            }
            icon={<FaBolt />}
            accent={C.green}
            spark={rpmSpark}
            sparkColor={C.green}
          />
        </div>

        {/* Two-column row: Reviews list (wide) + Leaderboard (narrow) */}
        <div className="internal-mobile-stack"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)',
            gap: 18,
          }}
        >
          {/* Reviews list */}
          <div
            style={{
              background: C.card,
              border: `1px solid ${C.line}`,
              borderRadius: 12,
              boxShadow: C.shadow,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 18px',
                borderBottom: `1px solid ${C.line}`,
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, color: C.ink }}>
                <FaCommentDots style={{ color: C.navy }} /> Reviews
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                  <select
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                    aria-label="Filter by month"
                    style={{
                      background: monthFilter !== 'all' ? C.navy : C.lineSoft,
                      color: monthFilter !== 'all' ? '#fff' : C.inkSoft,
                      border: monthFilter !== 'all' ? 'none' : `1px solid ${C.line}`,
                      borderRadius: 999,
                      padding: '5px 28px 5px 12px',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      font: 'inherit',
                    }}
                  >
                    <option value="all">All time</option>
                    {availableMonths.map((m) => (
                      <option key={m.key} value={m.key}>{m.label}</option>
                    ))}
                  </select>
                  <FaChevronDown
                    style={{
                      position: 'absolute', right: 11, fontSize: 10, pointerEvents: 'none',
                      color: monthFilter !== 'all' ? '#fff' : C.muted,
                    }}
                  />
                </div>
                {selectedTech !== 'all' && (
                  <button
                    type="button"
                    onClick={() => setSelectedTech('all')}
                    style={{
                      background: C.blueSoft,
                      color: C.navy,
                      border: `1px solid ${C.line}`,
                      borderRadius: 999,
                      padding: '4px 10px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {selectedTech} <FaTimes style={{ fontSize: 10 }} />
                  </button>
                )}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: C.lineSoft,
                    border: `1px solid ${C.line}`,
                    borderRadius: 8,
                    padding: '4px 10px',
                  }}
                >
                  <FaSearch style={{ color: C.muted, fontSize: 11 }} />
                  <input
                    type="text"
                    value={textFilter}
                    onChange={(e) => setTextFilter(e.target.value)}
                    placeholder="Search text or author…"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      outline: 'none',
                      fontSize: 12,
                      width: 180,
                      color: C.ink,
                      font: 'inherit',
                    }}
                  />
                </div>
                <span style={{ fontSize: 12, color: C.muted }}>
                  {feedLoading ? 'Loading…' : `${visibleReviews.length} of ${reviews.length}`}
                </span>
              </div>
            </div>
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {feedLoading ? (
                <div style={{ padding: 28, color: C.muted, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <FaSpinner className="hudson-spin" /> Loading reviews…
                </div>
              ) : feedError ? (
                <div style={{ padding: 28, color: C.red, fontSize: 14 }}>{feedError}</div>
              ) : !connected ? (
                <div style={{ padding: 28, color: C.muted, fontSize: 14, textAlign: 'center' }}>
                  Add <code>GOOGLE_PLACES_API_KEY</code> to Heroku to start
                  tracking reviews from the Google Places API.
                </div>
              ) : visibleReviews.length === 0 ? (
                <div style={{ padding: 28, color: C.muted, fontSize: 14, textAlign: 'center' }}>
                  {reviews.length === 0
                    ? 'No reviews archived yet. Hit "Sync now" to pull them from Google.'
                    : 'No reviews match the current filters.'}
                </div>
              ) : (
                visibleReviews.map((r, idx) => (
                  <ReviewCard
                    key={r.reviewId || `${r.authorName || 'anon'}-${idx}`}
                    review={r}
                    onTechClick={(t) => setSelectedTech(t)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Leaderboard */}
          <div
            style={{
              background: C.card,
              border: `1px solid ${C.line}`,
              borderRadius: 12,
              boxShadow: C.shadow,
              overflow: 'hidden',
              alignSelf: 'start',
            }}
          >
            <div
              style={{
                padding: '14px 18px',
                borderBottom: `1px solid ${C.line}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, color: C.ink }}>
                <FaTrophy style={{ color: C.gold }} /> Technician leaderboard
              </div>
              <span style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                {monthFilter === 'all'
                  ? `Mentions across ${reviews.length.toLocaleString()} reviews`
                  : (availableMonths.find((m) => m.key === monthFilter)?.label || 'Selected month')}
              </span>
            </div>
            {techLoading || feedLoading ? (
              <div style={{ padding: 24, color: C.muted, fontSize: 13 }}>Loading…</div>
            ) : leaderboard.length === 0 ? (
              <div style={{ padding: 24, color: C.muted, fontSize: 13, textAlign: 'center' }}>
                No technicians have been mentioned by name yet.
              </div>
            ) : (
              <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {leaderboard.map((row, idx) => (
                  <LeaderboardRow
                    key={row.tech}
                    rank={idx + 1}
                    tech={row.tech}
                    count={row.count}
                    avgRating={row.avgRating}
                    lastTime={row.lastTime}
                    isSelected={selectedTech === row.tech}
                    onClick={() => setSelectedTech((cur) => (cur === row.tech ? 'all' : row.tech))}
                  />
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>

      {shareSnackbar && (
        <div
          style={{
            position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
            background: C.green, color: '#fff', fontSize: 13, fontWeight: 600,
            padding: '10px 16px', borderRadius: 999,
            display: 'inline-flex', alignItems: 'center', gap: 8,
            boxShadow: '0 12px 28px rgba(20,30,60,.18)', zIndex: 1000,
          }}
        >
          <FaCheckCircle /> Leaderboard copied to clipboard
        </div>
      )}

      <style>{`
        @keyframes hudsonSpin { to { transform: rotate(360deg); } }
        .hudson-spin { animation: hudsonSpin 1s linear infinite; display: inline-block; }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Pill({ label, value, sub, icon, accent = C.navy, spark, sparkColor }) {
  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.line}`,
        borderRadius: 12,
        padding: '14px 16px',
        boxShadow: C.shadow,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minHeight: 130,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.05em' }}>
          {label}
        </div>
        <div
          style={{
            width: 24, height: 24, borderRadius: 8,
            background: `${accent}14`, color: accent,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12,
          }}
        >
          {icon}
        </div>
      </div>
      <div style={{ fontSize: 30, fontWeight: 700, color: C.ink, lineHeight: 1.05, letterSpacing: '-0.02em' }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: C.muted, minHeight: 18 }}>{sub}</div>
      {spark ? <MiniSpark points={spark} color={sparkColor || accent} /> : null}
    </div>
  );
}

function ReviewCard({ review, onTechClick }) {
  const {
    authorName, authorPhotoUrl, starRating, comment,
    createTime, replyText, replyTime, matched_techs = [],
    edited, updateTime,
  } = review;
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        padding: '16px 18px',
        borderBottom: `1px solid ${C.line}`,
      }}
    >
      {authorPhotoUrl ? (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img
          src={authorPhotoUrl}
          alt={authorName}
          style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }}
          referrerPolicy="no-referrer"
        />
      ) : (
        <FaUserCircle style={{ fontSize: 40, color: C.line, flexShrink: 0 }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ color: C.ink, fontWeight: 700, fontSize: 14 }}>{authorName || 'Anonymous'}</span>
          {typeof starRating === 'number' && <StarRow rating={starRating} size={13} />}
          {createTime && <span style={{ color: C.muted, fontSize: 12 }}>· {relativeAgo(createTime)}</span>}
          {edited && (
            <span
              title={updateTime ? `Edited ${relativeAgo(updateTime)}` : 'Edited'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 11, fontWeight: 600, color: '#9A6700',
                background: '#FFF4D6', border: '1px solid #F2D98A',
                borderRadius: 999, padding: '1px 8px',
              }}
            >
              ✎ Edited{updateTime ? ` ${relativeAgo(updateTime)}` : ''}
            </span>
          )}
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, color: C.muted, fontSize: 11 }}>
            <FaGoogle style={{ color: '#4285F4' }} /> Google
          </span>
        </div>
        {comment && (
          <p style={{ margin: '6px 0 0', fontSize: 14, color: C.inkSoft, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
            {comment}
          </p>
        )}
        {replyText && (
          <div
            style={{
              marginTop: 8,
              background: C.lineSoft,
              border: `1px solid ${C.line}`,
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 13,
              color: C.inkSoft,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 11, color: C.muted, marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <FaReply /> Hudson replied{replyTime ? ` · ${relativeAgo(replyTime)}` : ''}
            </div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{replyText}</div>
          </div>
        )}
        {matched_techs.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {matched_techs.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => onTechClick?.(t)}
                style={{
                  background: C.blueSoft,
                  color: C.navy,
                  border: `1px solid ${C.line}`,
                  borderRadius: 999,
                  padding: '3px 10px',
                  fontSize: 11.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
                title={`Filter reviews mentioning ${t}`}
              >
                <FaTrophy style={{ fontSize: 9 }} /> {t}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LeaderboardRow({ rank, tech, count, avgRating, lastTime, isSelected, onClick }) {
  const isMedal = rank <= 3;
  const medalColor = rank === 1 ? C.gold : rank === 2 ? C.silver : rank === 3 ? C.bronze : null;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          width: '100%',
          padding: '12px 16px',
          background: isSelected ? C.blueSoft : 'transparent',
          border: 'none',
          borderBottom: `1px solid ${C.line}`,
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'background 120ms',
        }}
        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = C.lineSoft; }}
        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
      >
        <div
          style={{
            width: 30, height: 30, borderRadius: 8,
            background: isMedal ? `${medalColor}1A` : C.lineSoft,
            color: isMedal ? medalColor : C.muted,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 14, flexShrink: 0,
          }}
        >
          {isMedal ? <FaMedal /> : rank}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.ink, lineHeight: 1.2 }}>{tech}</div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
            {avgRating != null && <>
              <StarRow rating={avgRating} size={10} /> {avgRating.toFixed(1)}
            </>}
            {avgRating != null && lastTime ? <span>·</span> : null}
            {lastTime ? <span>last {relativeAgo(lastTime / 1000)}</span> : null}
          </div>
        </div>
        <div
          style={{
            background: isMedal ? `${medalColor}22` : C.blueSoft,
            color: isMedal ? medalColor : C.navy,
            fontWeight: 700,
            fontSize: 13,
            padding: '3px 10px',
            borderRadius: 999,
            flexShrink: 0,
          }}
        >
          {count}
        </div>
      </button>
    </li>
  );
}
